import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CloudUpload,
  Download,
  Description,
  GetApp,
  CheckCircle,
  Clear,
  Assignment,
  Inventory,
  Euro,
  DesignServices,
  TableChart,
  TrendingUp,
  LocalOffer
} from '@mui/icons-material';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';
import API_BASE_URL from '../config/api';
import { useActivityLog } from '../context/ActivityLogContext';

const ItemsDetail = () => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const {
    addActivityProcess,
    updateActivityProcess,
    addLog,
    completeActivityProcess,
  } = useActivityLog();

  const [processingMode, setProcessingMode] = useState('eu'); // 'eu' or 'design'
  const [importFile, setImportFile] = useState(null);
  const [importFilePath, setImportFilePath] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [results, setResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];

    const isValidType = allowedTypes.some(type => file.type === type) ||
      file.name.toLowerCase().endsWith('.xlsx') ||
      file.name.toLowerCase().endsWith('.xls') ||
      file.name.toLowerCase().endsWith('.csv');

    if (!isValidType) {
      setError('Please select a valid Excel (.xlsx, .xls) or CSV (.csv) file.');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setError('File size must be less than 50MB');
      return;
    }

    setImportFile(file);
    setImportFilePath(file.name);
    setError('');
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      validateAndSetFile(droppedFiles[0]);
    }
  };

  const handleProcessFile = async () => {
    if (!importFile) {
      setError('Please select an import file before processing.');
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');
    setResults(null);

    const modeLabel = processingMode === 'eu' ? 'EU Items Export' : 'Design Items Export';
    const processId = `items-detail-${processingMode}-${Date.now()}`;

    // Add to global activity log
    addActivityProcess(processId, modeLabel, 'Items Detail');
    addLog(processId, `Starting ${modeLabel} processing...`);
    addLog(processId, `File: ${importFile.name}`);

    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const endpoint = processingMode === 'eu'
        ? `${API_BASE_URL}/api/ItemsDetail/process-eu-items`
        : `${API_BASE_URL}/api/ItemsDetail/process-design-items`;

      addLog(processId, `Loading master data from S3...`);
      updateActivityProcess(processId, { progressPercentage: 20 });

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        },
        body: formData
      });

      updateActivityProcess(processId, { progressPercentage: 60 });

      if (!response.ok) {
        // Try to read error as text first, then parse as JSON
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || `Server error: ${response.status}`;
        } catch {
          errorMessage = errorText || `Server error: ${response.status}`;
        }
        addLog(processId, `Error: ${errorMessage}`);
        completeActivityProcess(processId, false);
        throw new Error(errorMessage);
      }

      addLog(processId, `Processing complete, downloading file...`);
      updateActivityProcess(processId, { progressPercentage: 80 });

      // Auto-download the Excel file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      const timestamp = now.getFullYear().toString() +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0') + '_' +
        String(now.getHours()).padStart(2, '0') +
        String(now.getMinutes()).padStart(2, '0') +
        String(now.getSeconds()).padStart(2, '0');
      const fileName = processingMode === 'eu'
        ? `EU_Items_Detail_${timestamp}.xlsx`
        : `Design_Items_Detail_${timestamp}.xlsx`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      addLog(processId, `File downloaded: ${fileName}`);
      updateActivityProcess(processId, { progressPercentage: 100 });
      completeActivityProcess(processId, true);

      setSuccess(`Processing completed! ${modeLabel} file downloaded successfully.`);
      setResults({
        success: true,
        mode: processingMode,
        fileName: fileName
      });

    } catch (err) {
      let displayMessage = err.message;
      if (err.message === 'Failed to fetch') {
        displayMessage = 'Server unreachable or request timed out. The server may be processing a large file - please try again.';
      }
      setError(`Processing failed: ${displayMessage}`);
      addLog(processId, `Error: ${displayMessage}`);
      completeActivityProcess(processId, false);
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const endpoint = processingMode === 'eu'
        ? `${API_BASE_URL}/api/ItemsDetail/download-eu-sample`
        : `${API_BASE_URL}/api/ItemsDetail/download-design-sample`;

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || `Server error: ${response.status}`;
        } catch {
          errorMessage = errorText || `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = processingMode === 'eu'
        ? 'EU_Items_Template.xlsx'
        : 'Design_Items_Template.xlsx';
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };

  const resetForm = () => {
    setImportFile(null);
    setImportFilePath('');
    setError('');
    setSuccess('');
    setResults(null);
  };

  const getModeDescription = () => {
    if (processingMode === 'eu') {
      return 'Process EU items by importing Item IDs to get detailed information including dimensions, pricing, and EU-specific mappings';
    } else {
      return 'Process design items by importing Design IDs to get all items associated with those designs';
    }
  };

  const getSampleColumn = () => {
    return processingMode === 'eu' ? 'ItemID' : 'DesignID';
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Items Detail Export" category="excel" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          ITEMS DETAIL EXPORT
        </Typography>

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Export Configuration
              </Typography>

              {/* Processing Mode Selection */}
              <Card sx={{ mb: 3, bgcolor: getCardBackground(theme) }}>
                <CardContent>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary } }}>
                      Select Processing Mode
                    </FormLabel>
                    <RadioGroup
                      value={processingMode}
                      onChange={(e) => {
                        setProcessingMode(e.target.value);
                        setImportFile(null);
                        setImportFilePath('');
                        setError('');
                        setSuccess('');
                        setResults(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      row
                      sx={{ mt: 1 }}
                    >
                      <FormControlLabel
                        value="eu"
                        control={<Radio sx={{ color: alpha(TOOL_COLORS.EXCEL.primary, 0.7), '&.Mui-checked': { color: TOOL_COLORS.EXCEL.primary } }} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Euro sx={{ color: TOOL_COLORS.EXCEL.primary }} />
                            <span>EU Items</span>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="design"
                        control={<Radio sx={{ color: alpha(TOOL_COLORS.EXCEL.primary, 0.7), '&.Mui-checked': { color: TOOL_COLORS.EXCEL.primary } }} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DesignServices sx={{ color: TOOL_COLORS.EXCEL.primary }} />
                            <span>Design Items</span>
                          </Box>
                        }
                      />
                    </RadioGroup>
                    <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                      {getModeDescription()}
                    </Typography>
                  </FormControl>
                </CardContent>
              </Card>

              {/* File Upload */}
              <Card sx={{ mb: 3, bgcolor: importFile ? getCardBackground(theme) : 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description sx={{ color: TOOL_COLORS.EXCEL.primary }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Import File (Excel/CSV)
                    </Typography>
                    {importFile && (
                      <Tooltip title="File selected">
                        <CheckCircle color="success" />
                      </Tooltip>
                    )}
                  </Box>

                  <input
                    type="file"
                    hidden
                    ref={fileInputRef}
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileUpload}
                  />

                  {/* Drag and Drop Zone */}
                  <Box
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: `2px dashed ${isDragging ? TOOL_COLORS.EXCEL.primary : importFile ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      cursor: 'pointer',
                      bgcolor: isDragging ? TOOL_COLORS.EXCEL.light : importFile ? '#4CAF5010' : 'transparent',
                      transition: 'all 0.3s ease',
                      mb: 2,
                      '&:hover': {
                        borderColor: importFile ? '#4CAF50' : TOOL_COLORS.EXCEL.primary,
                        bgcolor: importFile ? '#4CAF5015' : TOOL_COLORS.EXCEL.light,
                      },
                    }}
                  >
                    {importFile ? (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          1 file selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click to replace
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.EXCEL.primary : theme.palette.text.secondary, mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          {isDragging ? 'Drop file here' : 'Drag & drop file here or click to select'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Excel (.xlsx, .xls) or CSV files only
                        </Typography>
                      </>
                    )}
                  </Box>

                  {importFile && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 1.5,
                        p: 1,
                        bgcolor: 'background.paper',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Description sx={{ color: TOOL_COLORS.EXCEL.primary, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {importFile.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                          ({(importFile.size / 1024 / 1024).toFixed(2)} MB)
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => { setImportFile(null); setImportFilePath(''); if (fileInputRef.current) fileInputRef.current.value = ''; }} sx={{ color: theme.palette.error.main }}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleDownloadSample}
                      startIcon={<GetApp sx={{ fontSize: '18px !important' }} />}
                      sx={{
                        borderColor: alpha(theme.palette.text.primary, 0.2),
                        color: alpha(theme.palette.text.primary, 0.7),
                        bgcolor: 'transparent',
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2.5,
                        py: 0.8,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: alpha(theme.palette.text.primary, 0.4),
                          color: theme.palette.text.primary,
                          bgcolor: alpha(theme.palette.text.primary, 0.05),
                        },
                        '&:active': {
                          transform: 'scale(0.98)',
                        },
                      }}
                    >
                      Download Template
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              {/* Process Button */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleProcessFile}
                  disabled={!importFile || processing}
                  sx={{
                    bgcolor: TOOL_COLORS.EXCEL.primary,
                    '&:hover': { bgcolor: TOOL_COLORS.EXCEL.hover },
                    px: 4
                  }}
                >
                  {processing ? 'Processing...' : 'Generate Export'}
                </Button>

                {(importFile || results) && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05)
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Status & Information Sidebar */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Information
              </Typography>

              {/* Progress */}
              {processing && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Processing {processingMode === 'eu' ? 'EU items' : 'design items'}...
                  </Typography>
                  <LinearProgress color="primary" />
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {/* Results Summary */}
              {results && (
                <Box sx={{ mb: 3 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    File has been automatically downloaded. Check your downloads folder.
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Feature Information */}
              {processingMode === 'eu' ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    EU Items Processing:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <TableChart sx={{ color: TOOL_COLORS.EXCEL.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Load Master Data"
                        secondary="US and EU items detail data"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Inventory sx={{ color: TOOL_COLORS.EXCEL.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Map EU Items"
                        secondary="Links US items to EU equivalents"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <TrendingUp sx={{ color: TOOL_COLORS.EXCEL.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Enrich Data"
                        secondary="Adds dimensions, weight, pricing"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Download sx={{ color: TOOL_COLORS.EXCEL.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Generate Export"
                        secondary="Creates comprehensive Excel file"
                      />
                    </ListItem>
                  </List>
                </Box>
              ) : (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Design Items Processing:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <DesignServices sx={{ color: TOOL_COLORS.EXCEL.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Design ID Lookup"
                        secondary="Matches by WebID and DESIGN fields"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Assignment sx={{ color: TOOL_COLORS.EXCEL.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Collect Items"
                        secondary="Gathers all items for each design"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <TableChart sx={{ color: TOOL_COLORS.EXCEL.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Complete Details"
                        secondary="Full item specifications included"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Download sx={{ color: TOOL_COLORS.EXCEL.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Export Results"
                        secondary="Downloads Excel with all details"
                      />
                    </ListItem>
                  </List>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Expected File Format */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Expected File Format:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                Import file should contain:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                <Chip
                  label={getSampleColumn()}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', borderColor: TOOL_COLORS.EXCEL.primary, color: TOOL_COLORS.EXCEL.primary }}
                />
              </Box>

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Instructions:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                1. Select processing mode (EU Items or Design Items)<br />
                2. Download template file<br />
                3. Fill template with {processingMode === 'eu' ? 'Item IDs' : 'Design IDs'}<br />
                4. Upload populated file<br />
                5. Generate and download export<br /><br />
                <strong>Note:</strong> Results include complete item specifications, dimensions, and pricing information.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ItemsDetail;
